import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import Select from "react-select";
import { checkFridgeCode } from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faChevronDown, faPlus, faTimes, faSpinner  } from "@fortawesome/free-solid-svg-icons";

const TopMenu = ({ onFridgeSelect }) => {
    const MIN_CODE_LEN = 4;
    const DEBOUNCE_MS = 350;
    const debounceRef = useRef(null);
    const lastFetchedRef = useRef("");

    const baseFridgeAddAdditional = `https://boxmealadminapinew.azurewebsites.net/api/fridge/additional-fridge`;
    const baseFridgeGetByUserId = `https://boxmealadminapinew.azurewebsites.net/api/fridge/by-user`;
    const baseIsVoucherFridgeByFridgeId = `https://boxmealadminapinew.azurewebsites.net/api/fridge`;

    const { user } = useAuth();
    const { setCartItems } = useCart();

    const [fridges, setFridges] = useState([]);
    const initialFridge = localStorage.getItem("selectedFridgeId") || "";
    const [selectedFridge, setSelectedFridge] = useState(initialFridge);

    const [showInlineAdder, setShowInlineAdder] = useState(false);

    const [fridgeDetails, setFridgeDetails] = useState(null);
    const [fridgeName, setFridgeName] = useState("");
    const [fridgeCode, setFridgeCode] = useState("");
    const [fridgeNameConfirmation, setFridgeNameConfirmation] = useState("");
    const [isFridgeCodeValid, setIsFridgeCodeValid] = useState(true);

    const [isCheckingCode, setIsCheckingCode] = useState(false);

    const userId = user?.id;

    // Track mounted to avoid side-effects after unmount
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // Always fetch and persist the voucher flag for a fridge id
    const fetchVoucherFlag = useCallback(async (fridgeId) => {
        if (!fridgeId) return;
        try {
            const resp = await fetch(`${baseIsVoucherFridgeByFridgeId}/${fridgeId}/voucher-flag`);
            console.log("TopMenu: fetchVoucherFlag response", fridgeId, resp.status);
            if (!resp.ok) return;
            const data = await resp.json();
            console.log("TopMenu: fetchVoucherFlag data", { data });
            const flag = !!data.isVoucherFridge;
            if (mountedRef.current) {
                localStorage.setItem("isVoucherFridge", JSON.stringify(flag));
            }
            console.log("TopMenu: fetchVoucherFlag saved", { id: fridgeId, api: data.isVoucherFridge, saved: flag, ls: localStorage.getItem("isVoucherFridge") });
        } catch (e) {
            console.warn("TopMenu: fetchVoucherFlag failed", e);
        }
    }, []);

    const fetchFridges = useCallback(async () => {
        if (!userId) return [];
        try {
            const res = await fetch(`${baseFridgeGetByUserId}/${userId}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            console.log("TopMenu: fetched fridges (raw)", data);
            const options = (Array.isArray(data) ? data : []).map(f => ({
                label: f.fridgeName,
                value: f.fridgeId,
                // Only keep boolean; treat null/undefined as unknown to avoid false due to !!null
                isVoucherFridge: (typeof f.isVoucherFridge === "boolean") ? f.isVoucherFridge : undefined,
            }));
            console.log("TopMenu: fetched fridges (by-user)", options);
            setFridges(options);
            return options;
        } catch (e) {
            console.error("TopMenu: error fetching fridges", e);
            return fridges;
        }
    }, [userId]);

    useEffect(() => {
        fetchFridges();
    }, [fetchFridges]);

    useEffect(() => {
        if (!fridges.length) return;
        const exists = fridges.some(f => String(f.value) === String(selectedFridge));
        if (!exists) {
            const first = fridges[0];
            setSelectedFridge(first.value);
            localStorage.setItem("selectedFridgeId", first.value);
            // Optimistic write (if present as a real boolean), then always verify
            if (typeof first.isVoucherFridge === "boolean") {
                localStorage.setItem("isVoucherFridge", JSON.stringify(first.isVoucherFridge));
            }
            fetchVoucherFlag(first.value);
            console.log("TopMenu: defaulting to first fridge", { id: first.value, isVoucherFridge: first.isVoucherFridge, ls: localStorage.getItem("isVoucherFridge") });
            onFridgeSelect?.(first.value);
        } else {
            const current = fridges.find(f => String(f.value) === String(selectedFridge));
            if (current) {
                // Optimistic write (if present as a real boolean), then always verify
                if (typeof current.isVoucherFridge === "boolean") {
                    localStorage.setItem("isVoucherFridge", JSON.stringify(current.isVoucherFridge));
                    console.log("TopMenu: syncing current fridge voucher flag (optimistic)", { id: current.value, isVoucherFridge: current.isVoucherFridge, ls: localStorage.getItem("isVoucherFridge") });
                } else {
                    console.log("TopMenu: current fridge has unknown voucher flag from by-user (null/undef), awaiting server verify");
                }
                fetchVoucherFlag(current.value);
            }
        }
    }, [fridges, selectedFridge, fetchVoucherFlag, onFridgeSelect]);

    const [isHidden, setIsHidden] = useState(false);

    useEffect(() => {
        const onScroll = () => setIsHidden((window.scrollY || 0) > 100);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const h = isHidden ? "0px" : "56px";
        document.documentElement.style.setProperty("--top-menu-h", h);
        return () => document.documentElement.style.removeProperty("--top-menu-h");
    }, [isHidden]);

    const handleFridgeChange = async (selectedOption) => {
        if (!selectedOption) return;
        const selectedValue = selectedOption.value;
        // Clear cart only if fridge actually changes
        if (String(selectedValue) !== String(selectedFridge)) {
            setCartItems([]);
            console.log("TopMenu: cart cleared due to fridge change", { prev: selectedFridge, next: selectedValue });
        }
        setSelectedFridge(selectedValue);
        localStorage.setItem("selectedFridgeId", selectedValue);
        // Optimistic write from option only when it's a real boolean; always verify via API
        if (typeof selectedOption.isVoucherFridge === "boolean") {
            const savedFlag = selectedOption.isVoucherFridge;
            localStorage.setItem("isVoucherFridge", JSON.stringify(savedFlag));
            console.log("TopMenu: handleFridgeChange - optimistic option flag", { id: selectedValue, isVoucherFridge: savedFlag, ls: localStorage.getItem("isVoucherFridge") });
        } else {
            console.log("TopMenu: handleFridgeChange - option has unknown voucher flag (null/undef), will verify via API");
        }
        // Always verify current truth from server
        fetchVoucherFlag(selectedValue);
        onFridgeSelect?.(selectedValue);
    };

    const handleFridgeCodeChange = (e) => {
        let code = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
        setFridgeCode(code);
    };

    const handleFetchFridgeName = useCallback(async (codeArg) => {
        const code = (codeArg ?? fridgeCode).toUpperCase().replace(/[^A-Z0-9]/g, "");

        if (code.length > 0 && /^[A-Za-z0-9]+$/.test(code)) {
            try {
                setIsCheckingCode(true);
                const result = await checkFridgeCode(code);
                if (result && result.fridgeName) {
                    setFridgeName(result.fridgeName);
                    setFridgeNameConfirmation(result.fridgeName);
                    setFridgeDetails(result);
                    setIsFridgeCodeValid(true);
                } else {
                    setFridgeName("");
                    setFridgeNameConfirmation("");
                    setFridgeDetails(null);
                    setIsFridgeCodeValid(false);
                }
            } catch (error) {
                console.error("Error checking fridge code:", error);
                setFridgeName("");
                setFridgeNameConfirmation("");
                setFridgeDetails(null);
                setIsFridgeCodeValid(false);
            } finally {
                setIsCheckingCode(false);
            }
        } else {
            setFridgeName("");
            setFridgeNameConfirmation("");
            setFridgeDetails(null);
            setIsFridgeCodeValid(false);
            setIsCheckingCode(false);
        }
    }, [fridgeCode]);

    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }

        const code = fridgeCode.toUpperCase().replace(/[^A-Z0-9]/g, "");
        const looksValid = /^[A-Za-z0-9]+$/.test(code);

        if (code.length >= MIN_CODE_LEN && looksValid) {
            setIsCheckingCode(true);
            debounceRef.current = setTimeout(async () => {
                if (code !== lastFetchedRef.current) {
                    await handleFetchFridgeName(code);
                    lastFetchedRef.current = code;
                } else {
                    setIsCheckingCode(false);
                }
            }, DEBOUNCE_MS);
        } else {
            setFridgeName("");
            setFridgeNameConfirmation("");
            setFridgeDetails(null);
            setIsFridgeCodeValid(true);
            setIsCheckingCode(false);
        }

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [fridgeCode, DEBOUNCE_MS, handleFetchFridgeName]);

    const handleConnectFridge = async () => {
        if (!userId || !fridgeDetails?.fridgeId) {
            alert("Missing user or fridge information.");
            return;
        }

        try {
            const payload = { userId, fridgeId: fridgeDetails.fridgeId };
            const response = await fetch(baseFridgeAddAdditional, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorText = await response.text().catch(() => "");
                console.error("Failed to add unit:", errorText);
                alert("❌ Failed to add unit.");
                return;
            }

            let body = null;
            try { body = await response.json(); } catch {}

            const newId    = body?.fridgeId ?? body?.id ?? fridgeDetails.fridgeId;
            const newLabel = body?.fridgeName ?? body?.name ?? fridgeDetails.fridgeName;
            const newIsVoucherFridge = !!(body?.isVoucherFridge);

            const options = await fetchFridges();
            if (!options.some(o => String(o.value) === String(newId))) {
                setFridges(prev => [...prev, { label: newLabel, value: newId, isVoucherFridge: newIsVoucherFridge }]);
            }

            setSelectedFridge(newId);
            localStorage.setItem("selectedFridgeId", newId);
            // Clear cart when switching to the newly connected fridge
            setCartItems([]);
            console.log("TopMenu: cart cleared due to connecting/selecting new fridge", { id: newId });
            // Optimistic write from server response, then verify
            localStorage.setItem("isVoucherFridge", JSON.stringify(newIsVoucherFridge));
            fetchVoucherFlag(newId);
            onFridgeSelect?.(newId);

            setShowInlineAdder(false);
            setFridgeCode("");
            setFridgeName("");
            setFridgeNameConfirmation("");
            setFridgeDetails(null);
            setIsFridgeCodeValid(true);
        } catch (error) {
            console.error("Error connecting to fridge:", error);
            alert("❌ Error connecting to fridge.");
        }
    };

    const clearInlineAdder = () => {
        setFridgeCode("");
        setFridgeName("");
        setFridgeNameConfirmation("");
        setFridgeDetails(null);
        setIsFridgeCodeValid(true);
        setShowInlineAdder(false);
        setIsCheckingCode(false);
    };

    return (
        <>
            <Wrap>
                <HeaderRow>
                    <Picker>
                        <LocationPin>
                            <FontAwesomeIcon icon={faMapMarkerAlt} />
                        </LocationPin>

                        <PickerLabel
                            title={fridges.find((f) => String(f.value) === String(selectedFridge))?.label || "Välj enhet"}
                        >
                            {fridges.find((f) => String(f.value) === String(selectedFridge))?.label || "Välj enhet"}
                        </PickerLabel>

                        <Chevron>
                            <FontAwesomeIcon icon={faChevronDown} />
                        </Chevron>

                        <Select
                            options={fridges}
                            value={fridges.find((f) => String(f.value) === String(selectedFridge))}
                            onChange={handleFridgeChange}
                            placeholder="Välj enhet…"
                            isClearable
                            isSearchable={false}
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            maxMenuHeight={280}
                            components={{ IndicatorSeparator: () => null }}
                            styles={{
                                container: (base) => ({ ...base, position: "absolute", inset: 0, zIndex: 5 }),
                                control: (base) => ({
                                    ...base,
                                    position: "absolute",
                                    inset: 0,
                                    height: "100%",
                                    minHeight: 0,
                                    background: "transparent",
                                    border: "none",
                                    boxShadow: "none",
                                    opacity: 0,
                                    cursor: "pointer",
                                }),
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                menu: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                    borderRadius: 12,
                                    boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
                                    overflow: "hidden",
                                    marginTop: 6,
                                }),
                                menuList: (base) => ({ ...base, padding: 6, userSelect: "none", WebkitUserSelect: "none" }),
                                option: (base, state) => ({
                                    ...base,
                                    padding: "12px 14px",
                                    fontSize: 16,
                                    lineHeight: 1.2,
                                    backgroundColor: state.isFocused ? "#f3f4f6" : "#fff",
                                    color: "#111827",
                                    ":active": { backgroundColor: "#e5e7eb" },
                                    userSelect: "none",
                                    WebkitTapHighlightColor: "transparent",
                                }),
                                indicatorsContainer: (b) => ({ ...b, display: "none" }),
                                placeholder: (b) => ({ ...b, display: "none" }),
                                singleValue: (b) => ({ ...b, display: "none" }),
                                valueContainer: (b) => ({ ...b, padding: 0 }),
                                input: (b) => ({ ...b, margin: 0, padding: 0 }),
                            }}
                        />
                    </Picker>

                    <RoundBtn
                        type="button"
                        title="Lägg till ny enhet"
                        aria-label="Lägg till ny enhet"
                        onClick={() => setShowInlineAdder((v) => !v)}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                    </RoundBtn>
                </HeaderRow>

                <InlineAdder $open={showInlineAdder}>
                    <InlineRow>
                        <InlineInputWrap $invalid={showInlineAdder && !isFridgeCodeValid && fridgeCode.length > 0}>
                            <InlineIconLeft>
                                <FontAwesomeIcon icon={faPlus} />
                            </InlineIconLeft>

                            <InlineInput
                                type="text"
                                placeholder="Lägg till enhet"
                                value={fridgeCode}
                                onChange={(e) => {
                                    handleFridgeCodeChange(e);
                                    setFridgeName("");
                                    setFridgeNameConfirmation("");
                                    setFridgeDetails(null);
                                }}
                                onBlur={() => handleFetchFridgeName()}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleFetchFridgeName();
                                    }
                                }}
                            />

                            {isCheckingCode && (
                                <InlineSpinner aria-hidden="true">
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                </InlineSpinner>
                            )}

                            <InlineClear type="button" aria-label="Clear" onClick={clearInlineAdder}>
                                <FontAwesomeIcon icon={faTimes} />
                            </InlineClear>
                        </InlineInputWrap>
                    </InlineRow>

                    {(fridgeNameConfirmation || fridgeDetails) && (
                        <PreviewCard>
                            <PreviewHeader>
                                <PreviewTitle>{fridgeNameConfirmation || fridgeDetails?.fridgeName}</PreviewTitle>
                                <PreviewSub>
                                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                                    <span>
                                        {fridgeDetails?.customerName || ""}
                                     </span>
                                </PreviewSub>
                            </PreviewHeader>

                            <AddUnitBtn onClick={handleConnectFridge}>Lägg till enhet</AddUnitBtn>
                        </PreviewCard>
                    )}
                </InlineAdder>
            </Wrap>
        </>
    );
};

export default TopMenu;

const Wrap = styled.div`
    background: #f6f7fa;
    border-bottom: 1px solid #eef2f6;
    transform: translateY(${({ $isHidden }) => ($isHidden ? "-100%" : "0")});
    opacity: ${({ $isHidden }) => ($isHidden ? "0" : "1")};
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    height: ${({ $isHidden }) => ($isHidden ? "0" : "auto")};
    overflow: hidden;
`;

const HeaderRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 42px;
    gap: 10px;
    align-items: center;
    padding: 10px 12px;
`;

const Picker = styled.div`
    position: relative;
    height: 44px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    padding: 0 40px 0 40px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    transition: all 0.3s ease;
    &:hover {
        background: rgba(255, 255, 255, 0.8);
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
        transform: translateY(-1px);
    }
`;

const LocationPin = styled.div`
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #374151;
    font-size: 14px;
    pointer-events: none;
`;

const PickerLabel = styled.div`
    font-size: 14px;
    font-weight: 400;
    color: #1e2330;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    pointer-events: none;
`;

const Chevron = styled.div`
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
    font-size: 10px;
    pointer-events: none;
`;

const InlineAdder = styled.div`
    padding: 0 12px 10px;
    overflow: hidden;
    transition:
            max-height 350ms cubic-bezier(0.34, 1.56, 0.64, 1),
            opacity 220ms ease;
    max-height: ${({ $open }) => ($open ? "260px" : "0px")};
    opacity: ${({ $open }) => ($open ? 1 : 0)};
`;

const InlineRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const InlineInputWrap = styled.div`
    position: relative;
    flex: 1;
    height: 42px;
    border-radius: 9999px;
    background: #fff;
    border: 1px solid ${({ $invalid }) => ($invalid ? "#ef4444" : "#e7ebf0")};
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
    display: flex;
    align-items: center;
    padding: 0 40px;
`;

const InlineIconLeft = styled.div`
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 14px;
    color: #9ca3af;
    pointer-events: none;
`;

const InlineClear = styled.button`
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: none;
    background: #f3f4f6;
    color: #6b7280;
    display: grid;
    place-items: center;
    cursor: pointer;
`;

const InlineInput = styled.input`
    flex: 1;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    font-size: 16px;        /* ⬅️ iOS zoom fix */
    line-height: 1.2;
    color: #111827;
    &::placeholder { color: #9ca3af; }
`;

const PreviewCard = styled.div`
    margin-top: 10px;
    padding: 12px;
    border: 1px solid #e7ebf0;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
`;

const PreviewHeader = styled.div`
    padding: 6px 4px 10px;
`;

const PreviewTitle = styled.div`
    font-weight: 700;
    color: #1f2937;
    font-size: 16px;
    line-height: 1.1;
`;

const PreviewSub = styled.div`
    margin-top: 2px;
    display: flex;
    align-items: center;
    gap: 6px;
    color: #6b7280;
    font-size: 13px;
`;

const AddUnitBtn = styled.button`
  margin-top: 8px;
  width: 100%;
  height: 44px;
  border-radius: 12px;
  background: #1f1f1f;
  color: #fff;
  border: none;
  font-weight: 700;
  cursor: pointer;
  transition: transform .08s ease, box-shadow .2s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(0,0,0,.12); }
  &:active { transform: translateY(0); box-shadow: none; }
`;

const RoundBtn = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  color: #1e2330;
  font-size: 16px;
  display: grid;
  place-items: center;
  cursor: pointer;
`;

const InlineSpinner = styled.div`
  position: absolute;
  right: 40px;     
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 16px;
  pointer-events: none;
`;
